import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;
    let timer;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center min-h-screen" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-14 w-14 text-[#60a5fa] mx-auto mb-8 animate-spin" strokeWidth={1} />
          <h1 className="font-display text-4xl text-white mb-4">Vérification du paiement…</h1>
          <p className="text-zinc-400">Merci de patienter quelques instants.</p>
        </>
      )}
      {state === "success" && (
        <>
          <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-8" strokeWidth={1} />
          <h1 className="font-display text-5xl text-white mb-4" data-testid="payment-success-title">Merci pour votre achat !</h1>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            Votre paiement a été confirmé. Un email de confirmation vous sera envoyé. Vous soutenez directement
            nos artistes — merci du fond du cœur.
          </p>
          <Link to="/galerie" className="inline-block bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 py-4 text-sm transition-colors">
            Retour à la galerie
          </Link>
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="h-16 w-16 text-[#E11D48] mx-auto mb-8" strokeWidth={1} />
          <h1 className="font-display text-4xl text-white mb-4">
            {state === "timeout" ? "Vérification en cours" : "Un problème est survenu"}
          </h1>
          <p className="text-zinc-400 mb-8">
            {state === "timeout"
              ? "Le statut du paiement met du temps à se mettre à jour. Vérifiez votre email pour la confirmation."
              : "Le paiement n'a pas pu être confirmé. Veuillez réessayer."}
          </p>
          <Link to="/panier" className="inline-block border border-white/20 hover:border-white text-white px-8 py-4 text-sm transition-colors">
            Retour au panier
          </Link>
        </>
      )}
    </div>
  );
}
